import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, firstValueFrom, tap} from 'rxjs';
import {MmFilterService} from '../../../mm-filter/mm-filter.service';
import {
  AppContainerOptions,
  AppRightPaneService,
  RightPaneContent,
  RightPaneContentVisibility,
  RightPaneHostDirective
} from '@mydocma/app-container';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {MmFaultRightPaneService} from '../mm-fault-right-pane/mm-fault-right-pane.service';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {MmFaultCameraPictureDialogComponent} from '../../mm-fault/mm-fault-camera-picture-dialog/mm-fault-camera-picture-dialog.component';
import {DatePipe} from '@angular/common';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {Attachment, AttachmentData} from '@docma-mm-core/attachment/attachment';
import {MmFilterModel} from '../../../mm-filter/mm-filter.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {Structure} from '@docma-mm-core/structure/structure';
import {MmFaultAttachmentDialogComponent} from '../../mm-fault/mm-fault-attachment-dialog/mm-fault-attachment-dialog.component';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {TranslateService} from '@ngx-translate/core';
import {MmFaultsLoaderService} from '../../mm-faults-loader.service';
import {filter} from 'rxjs/operators';
import {PoolEditDataService} from '../../../mm-filter/pool/services/pool-edit-data.service';
import {IPoolDefect} from '../../../mm-filter/pool/services/pool-api.service';
import {PoolFaultTrackerService} from '../../../mm-filter/pool/services/pool-fault-tracker.service';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {FieldDefaultNameEnum} from '@docma-mm-core/field/field-default-name.enum';
import {MmFaultsCountService} from './mm-faults-count.service';
import {MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {MmPlanEditService} from '../../mm-plans-view/mm-plan-view/services/mm-plan-edit.service';
import {MmFaultRightPanePageEnum} from '../mm-fault-right-pane/mm-fault-right-pane.enum';
import {FaultSyncService} from '@docma-mm-core/fault/sync/fault-sync.service';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {CurrentFaultsSyncStateService} from '../../current-faults-sync-state.service';
import {MmViewService} from '../../../mm-view.service';
import {GridApi} from 'ag-grid-community';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {MmFaultsViewHeaderComponent} from '../mm-faults-view-header/mm-faults-view-header.component';
import {BpPinLocationTargetEnum} from '../../../bp-common/models/bp-pin';
import {MmFaultTileExtendedComponent} from '../../mm-faults-tile-list/mm-fault-tile-extended/mm-fault-tile-extended.component';
import {MmBimService} from '../../../mm-bim/mm-bim.service';
import {MmPlansManagerService} from '../../mm-plans-view/services/mm-plans-manager.service';
import {MmFaultsSplitService} from './mm-faults-split.service';
import {Logger} from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Injectable()
export class MmFaultsViewService implements OnDestroy {

  public preventFlyToMarker = false;
  permissionDeleteAttachment$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.DeleteAttachment]);
  private loadingStateBehavior = new BehaviorSubject<boolean>(false);
  loadingState = this.loadingStateBehavior.asObservable();
  private appContainer: AppContainerOptions;
  private faultState = new BehaviorSubject<ViewFault>(null);
  currentFault = this.faultState.asObservable();

  // private faultsDiscardLoadStateSubject = new Subject<number>();
  // onDiscardLoadState = this.faultsDiscardLoadStateSubject.asObservable();
  private filter: MmFilterModel;

  public gridApi: GridApi;

  public popupComponent = MmFaultTileExtendedComponent;

  constructor(private readonly appRightPaneService: AppRightPaneService,
              private readonly matDialog: MatDialog,
              private readonly faultService: FaultService,
              private readonly attachmentService: AttachmentService,
              private readonly translateService: TranslateService,
              private readonly datepipe: DatePipe,
              public readonly permissionService: MmProjectPermissionsService,
              private readonly filterService: MmFilterService,
              private readonly dialogService: PromptDialogService,
              private readonly notficationService: NotificationService,
              private readonly faultsLoaderService: MmFaultsLoaderService,
              private readonly selectionService: SelectionService<ViewFault>,
              private readonly mmFaultRightPaneService: MmFaultRightPaneService,
              private readonly mmFaultsCountService: MmFaultsCountService,
              private readonly planEditService: MmPlanEditService,
              private readonly faultSyncService: FaultSyncService,
              private readonly currentFaultsSyncStateService: CurrentFaultsSyncStateService,
              private readonly mmViewService: MmViewService,
              private readonly deviceService: DeviceService,
              private bimService: MmBimService,
              private plansManagerService: MmPlansManagerService,
              private faultsSplitService: MmFaultsSplitService,
              private readonly poolEditDataService: PoolEditDataService,
              private readonly poolFaultTracker: PoolFaultTrackerService,
              private readonly logger: Logger) {

    this.planEditService.attachTo(this);
    this.plansManagerService.attachTo(this);
    this.faultsSplitService.attachTo(this);
    this.bimService.attachTo(this.plansManagerService);

    this.filterService.state.pipe(untilDestroyed(this)).subscribe(f => this.filter = f);
    this.faultService.onFaultDeleted
      .pipe(untilDestroyed(this))
      .subscribe(f => {
        const current = this.faultState.value;
        if (current &&
          ((f.id && f.id === current.id) || (f.uid && f.uid === current.uid))) {
          this.faultsLoaderService.currentFault = null;
          this.faultState.next(null);
          this.appRightPaneService.close();
        }
      });

    this.faultsLoaderService.loadingState
      .pipe(untilDestroyed(this))
      .subscribe(ls => this.loadingStateBehavior.next(ls));

    this.faultsLoaderService.faults
      .pipe(untilDestroyed(this))
      .subscribe(fs => this.onFaultsChanged(fs.loadedFaults));

    this.selectionService.selectionMode
      .pipe(
        untilDestroyed(this),
        filter(Boolean)
    ).subscribe(m => {
      // this.faultState.next(null);
    });

    this.faultSyncService.onDiscardLoadState
      .pipe(untilDestroyed(this))
      .subscribe(faultUids => {
        const allFaults: ViewFault[] = [];
        this.faultsLoaderService.faults.pipe(tap(x => x.loadedFaults.forEach(f => allFaults.push(f)))).toPromise();
        this.faultsLoaderService.discardLoadedFaults(faultUids);

        // faultUids.forEach(fUid => {
        //   const fault = allFaults.find(x => x.uid === fUid);
        //   if (fault && fault.loadState && (fault.loadState.status === LoadStatusEnum.Loaded || fault.loadState.status === LoadStatusEnum.Loading)) {
        //     console.warn('discard load state of fault - id: ' + fault.id + ', uid: ' + fault.uid);
        //     fault.loadState = new LoadState({status: LoadStatusEnum.NotLoaded});
        //   }
        //   // if (fault) {
        //   //   this.faultsDiscardLoadStateSubject.next(fault.id);
        //   // }
        // });
      });

    // MMW-1231: reset right pane completely on project switch
    this.mmViewService.currentProject$.pipe(
      untilDestroyed(this),
    ).subscribe(() => this.disposeRightPaneComponent());
  }

  ngOnDestroy(): void {
  }



  public registerRightPane(appContainer: AppContainerOptions, contents: RightPaneContent[]): void {

    this.unregisterRightPane();
    this.appContainer = appContainer;

    this.appContainer.rightPane.visibleRightPaneContentId = null;
    this.appContainer.rightPane.contents = contents;
  }

  public unregisterRightPane(): void {
    if (!this.appContainer) {
      return;
    }
    this.closeRightPane();
    this.appContainer.rightPane.visibleRightPaneContentId = null;
    this.appContainer.rightPane.contents = [];
    this.appContainer.notifyChanges();
    this.appContainer = null;
  }

  public async setCurrentFault(fault: ViewFault): Promise<boolean> {
    if (fault === this.faultState.value) {
      this.faultsLoaderService.currentFault = fault;
      this.faultState.next(fault);
      return true;
    }
    else if (await this.saveChanges()) {
      this.faultsLoaderService.currentFault = fault;
      this.faultState.next(fault);
      return true;
    }
    else if (!fault) {
      this.faultState.next(fault);
    }

    return false;
  }

  public async saveChanges(): Promise<boolean> {
    try {
      return await this.mmFaultRightPaneService.saveChanges();
    } catch (error) {
      this.notficationService.error(error);
      return false;
    }
  }

  public async openRightPane(node: ViewFault, tabIndex?: number, focusField?: string, undocked?: boolean) {
    if (!this.appContainer) {
      return;
    }
    if (await this.setCurrentFault(node)) {
      await this.mmFaultRightPaneService.openFirstPanePage(MmFaultRightPanePageEnum.fault, {
        fault: node,
        tabIndex,
        focusField
      }, undocked);
    }
  }

  public closeRightPane(): void {
    if (!this.appContainer) {
      return;
    }

    // hotfix adjustment for a bug in the mobile app (MMW-4657)
    // TODO_vm: investigate why setCurrentFault() triggers hasChanges during mobile pane close (hasChanges is true when there's no fault after creating first fault)
    // skip discard dialog during "create from plan" initialization on mobile only
    // mobile closes pane before user selects plan, causing plan changes to incorrectly trigger dialog
    // happens only when there's a fault being saved in background (after first fault creation)
    const skipDialogDuringInit = this.planEditService.isPlanEditInitializing && (this.deviceService.isMobil() || this.deviceService.isTablet());
    this.mmFaultRightPaneService.closePanePage(false, true, skipDialogDuringInit);
    // this.appRightPaneService.close();
  }

  async deleteFaults(faults: ViewFault[]) {
    try {
      this.loadingStateBehavior.next(true);

      await this.faultService.deleteFaults(faults[0].project.MyDocmaUid, faults[0].project.BPConnected, faults);
      this.selectionService.stopSelectionMode();
      this.notficationService.success(faults.length > 1 ?
        await this.translateService.get('MM.fault.deleteSuccessFaults').toPromise() :
        await this.translateService.get('MM.fault.deleteSuccessFault').toPromise());
      this.loadingStateBehavior.next(false);
      this.mmFaultsCountService.addDeletedFaults(faults.length);

      const savedFaults = faults.filter(f => f.id);
      if (savedFaults.length > 0) {
        this.removeFaultsFromPools(savedFaults);
      }

      return true;
    } catch (error) {
      this.notficationService.error(faults.length > 1 ? await this.translateService.get('MM.fault.deleteFailedFaults').toPromise() :
        await this.translateService.get('MM.fault.deleteFailedFault').toPromise());
      this.loadingStateBehavior.next(false);
      return false;
    }
  }

  private async removeFaultsFromPools(faults: ViewFault[]) {
    try {
      const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
      if (!currentProject) { return; }

      const faultIds = faults.map(f => f.id);
      const trackedPoolIds = this.poolFaultTracker.getTrackedPoolIds(faultIds);
      if (!trackedPoolIds || trackedPoolIds.size === 0) { return; }

      const defects: IPoolDefect[] = faults.map(f => ({
        Id: f.id,
        SUid: f.data?.SUid,
        UniqueId: f.uid
      }));

      const poolIds = Array.from(trackedPoolIds);
      const results = await Promise.allSettled(
        poolIds.map(poolId =>
          this.poolEditDataService.removeDefectsFromPool(currentProject.Id, poolId, defects)
            .then(() => poolId)
        )
      );

      const successfulPoolIds = results
        .filter((r): r is PromiseFulfilledResult<number> => r.status === 'fulfilled')
        .map(r => r.value);

      if (successfulPoolIds.length > 0) {
        const updatedPools = await this.poolEditDataService.getPoolsWithUpdatedCounts(currentProject.Id, successfulPoolIds);
        const poolCountChanges = updatedPools.map(pool => ({
          poolId: pool.Id,
          newCount: pool.Count
        }));
        this.faultsLoaderService.emitPoolCountUpdated(poolCountChanges);
      }

      faultIds.forEach(id => this.poolFaultTracker.clearFault(id));
    } catch (error) {
      this.logger.error(error, '', 'MmFaultsViewService');
    }
  }

  async onMenuItemClick() {
    const selectedNodes = this.gridApi?.getSelectedNodes();
    const isSelectionMode = await firstValueFrom(this.selectionService.selectionMode);
    if (isSelectionMode && selectedNodes && selectedNodes.length === 1) {
      this.selectionService.stopSelectionMode();
      this.gridApi.selectNode(selectedNodes[0]);
    }
  }

  async removePins(selectedFaults: ViewFault[]) {
    try {
      this.loadingStateBehavior.next(true);
      const planAttachments = [];

      /* MMW #433 remove attachment delete

      if(await lastValueFrom(this.permissionDeleteAttachment$.pipe(first()))) {

        const deleteAttachmentDialog = (await this.dialogService.showDeleteConfirmationDialog({
          title: this.translateService.instant('MM.dialog.removePlanAttachment.title'),
          content: this.translateService.instant('MM.dialog.removePlanAttachment.content')}))?.value;

        if(deleteAttachmentDialog) {
          selectedFaults.forEach(fault => {
            planAttachments = [...planAttachments, ...fault.attachments.filter(p => p.data.Type === AttachmentType.PlanSection)];
            fault.attachments = fault.attachments.filter(p => p.data.Type !== AttachmentType.PlanSection);
          });

          if(planAttachments.length > 0) {
            await this.faultViewService.deleteAttachments(planAttachments, selectedFaults[0]);
          }
        } else if(deleteAttachmentDialog === undefined) {
          this.loadingStateBehavior.next(false);
          return;
        }
      }*/

      selectedFaults.forEach(async fault => {

        if (fault.bpPin) {
          const bimSectionPlan = this.bimService.getSectionPlan(this.bimService.selectedPlan?.id.toString());
          const plan = this.plansManagerService.selectedPlan;
          const locIndex = fault.bpPin.Locations.findIndex(loc => loc.Type === BpPinLocationTargetEnum.plan);  // loc.ModelId === (bimSectionPlan?.planId || plan?.id?.toString()) && loc.VersionId === (bimSectionPlan?.versionId || plan?.data?.BpPlan?.activeVersion?.id)
          if (locIndex > -1) {
            fault.bpPin.Locations.splice(locIndex, 1);
            fault.addChanges({Pin: fault.bpPin, ModifiedOn: new Date()});
          }
        } else {
          fault.addChanges({
            PlanId: fault?.data?.PlanId || fault?.pin?.FileId,
            PlanPositionX: null, PlanPositionY: null,
            PlanRelativeAnchorToParentPositionX: null, PlanRelativeAnchorToParentPositionY: null
          });
        }

        // if(fault?.data?.PlanId) {
        //   fault.addChanges({
        //     PlanId: fault.data?.PlanId || fault.pin?.FileId,
        //     PlanPositionX: null, PlanPositionY: null,
        //     PlanRelativeAnchorToParentPositionX: null, PlanRelativeAnchorToParentPositionY: null
        //   });
        // } else if(fault?.bpPin?.Locations.find(l => l.Type == BpPinLocationTargetEnum.plan)) {
        //   fault.bpPin.Locations = null;
        //   fault.addChanges({Pin: fault.bpPin, ModifiedOn: new Date()})
        // }

        fault.pin = null;
        await this.faultService.saveFaultChanges(fault.project.MyDocmaUid, fault, true);
      });

      this.selectionService.stopSelectionMode();
      this.loadingStateBehavior.next(false);
    } catch (error) {
      this.notficationService.error(this.translateService.instant('MM.plan.error.removePins'));
      this.loadingStateBehavior.next(false);
    }
  }



  async createFaultFromAttachments(attachments?: File[], undocked?: boolean): Promise<void> {
    if (!await this.saveChanges()) {
      return;
    }

    const project = this.filter ? this.filter.project : null;
    if (!project) {
      throw new Error('Cann not create a new fault. No project is selected.');
    }

    let result: { attachmentData: AttachmentData, files: File[] };
    if (!undocked) {
      const dialogRef = this.matDialog.open(MmFaultAttachmentDialogComponent, {
        maxWidth: '100vw',
        panelClass: ['mydocma-dialog', 'mydocma-dialog-xs-fullscreen', 'mydocma-mm-fault-attachment-dialog'],
        autoFocus: false,
        disableClose: true
      });

      result = await dialogRef.afterClosed().toPromise();
    } else {
      result = {attachmentData: new AttachmentData(), files: attachments};
    }
    if (result) {
      try {
        const faultData = new FaultData();
        faultData.CreatedOn = new Date(Date.now());
        if (this.filter.structure && this.filter.structure.Id) {
          faultData.Structure = new Structure({Id: this.filter.structure.Id});
        } else {
          faultData.Structure = new Structure({Id: project.Structure.Id});
        }

        const fault = new ViewFault(await this.faultService.createNewFault(project.MyDocmaUid, faultData));
        fault.unsavedNew = true;

        result.attachmentData.Description = result.attachmentData.Description;
        result.attachmentData.CreatedOn = faultData.CreatedOn;
        fault.project = project;
        for (const f of result.files) {
          const attachment = await this.attachmentService.createNewAttachment(result.attachmentData, f);
          fault.addAttachment(attachment);
        }
        this.faultsLoaderService.addFault(fault);

        await this.openRightPane(fault, 0, FieldDefaultNameEnum.Description1, undocked);
      } catch (ex) {
        this.notficationService.error('Can not create a new fault');
      }
    }
  }

  async createFaultFromCamera(): Promise<void> {
    if (!await this.saveChanges()) {
      return;
    }

    const project = this.filter ? this.filter.project : null;
    if (!project) {
      throw new Error('Cann not create a new fault. No project is selected.');
    }

    const dialogRef: MatDialogRef<MmFaultCameraPictureDialogComponent> =
      this.matDialog.open(MmFaultCameraPictureDialogComponent, {
        width: '400px',
      });

    const attachment: Attachment = await dialogRef.afterClosed().toPromise();

    if (attachment) {
      const faultData = new FaultData();
      faultData.CreatedOn = attachment.changeState.date;
      if (this.filter.structure && this.filter.structure.Id) {
        faultData.Structure = new Structure({Id: this.filter.structure.Id});
      } else {
        faultData.Structure = new Structure({Id: project.Structure.Id});
      }

      const fault = new ViewFault(await this.faultService.createNewFault(project.MyDocmaUid, faultData));
      fault.project = project;
      fault.unsavedNew = true;
      fault.attachments = [];
      const attachmentData = attachment.changeState.data;
      attachmentData.Description = 'Foto_' + this.datepipe.transform(faultData.CreatedOn, 'yyyy-MM-dd HH:mm:ss');
      attachment.changeState.date = fault.changeState.date;
      attachment.changeState = new ChangeState(attachment.changeState);
      fault.addAttachment(attachment);
      await this.faultService.saveFaultChanges(fault.project.MyDocmaUid, fault, false);
      this.faultsLoaderService.addFault(fault);
      await this.openRightPane(fault, 0);
    }
  }

  async createFaultFromDescription(source?: string): Promise<void> {
    if (!await this.saveChanges()) {
      return;
    }

    const project = this.filter ? this.filter.project : null;
    if (!project) {
      throw new Error('Can not create a new fault. No project is selected.');
    }

    const faultData = new FaultData();
    if (this.filter.structure && this.filter.structure.Id) {
      faultData.Structure = new Structure({Id: this.filter.structure.Id});
    } else {
      faultData.Structure = new Structure({Id: project.Structure.Id});
    }

    const fault = new ViewFault(await this.faultService.createNewFault(project.MyDocmaUid, faultData));
    fault.project = project;
    fault.unsavedNew = true;

    if ((!this.deviceService.isMobil() && !this.deviceService.isTablet()) || source !== MmFaultsViewHeaderComponent.mmCreateFromPlan) {
      this.faultsLoaderService.addFault(fault);
      await this.openRightPane(fault, 0, FieldDefaultNameEnum.Description1);
    }
    else {
      await this.setCurrentFault(fault);
      this.faultsLoaderService.addFault(fault);
    }
  }

  private async onFaultsChanged(fs: ViewFault[]) {
    const current = this.faultState.value;
    if (current) {
      if (!fs) {
        this.faultsLoaderService.currentFault = null;
        this.faultState.next(null);
      } else {
        const index = fs.findIndex(f => (current.uid && f.uid === current.uid));
        let currentFaultNotSynced = false;
        if (this.currentFaultsSyncStateService.isSyncing) {
          const unsyncedFaults = (await firstValueFrom(this.currentFaultsSyncStateService.currentSyncState$)).syncState;
          for (const fUid in unsyncedFaults) {
            if (current?.uid === fUid) {
              currentFaultNotSynced = true;
            }
          }
        }

        // MMW-1204
        // #Case 1: Close right pane if selected fault is synced but cant be displayed
        // #Case 2: Close right pane if selected fault is newly created but not synced yet
        if ((index < 0 && !currentFaultNotSynced) || (index > -1 && currentFaultNotSynced && !fs[index]?.unsavedNew)) {
          this.faultsLoaderService.currentFault = null;
          this.faultState.next(null);
          this.closeRightPane();
        }
      }
    }
  }

  private disposeRightPaneComponent() {
    this.appRightPaneService.rightPaneCache.forEach((cacheItem) => {
      if (cacheItem && cacheItem.componentRef) {
        cacheItem.componentRef.destroy();
        cacheItem.id = RightPaneHostDirective.catalogPreemptiveId;
        cacheItem.componentRef = null;
        cacheItem.visibility = RightPaneContentVisibility.scoped;
      }
    });
  }

}
